import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { buttonVariants } from "~/components/ui/Button";
import { rewriteCf7FormActions } from "~/lib/cf7";
import { cn } from "~/lib/utils";

interface TurnstileApi {
  render: (
    container: HTMLElement | string,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      theme?: "light" | "dark" | "auto";
    }
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

const getTurnstile = (): TurnstileApi | undefined =>
  (window as unknown as { turnstile?: TurnstileApi }).turnstile;

interface WpContentProps {
  /** HTML renderizzato da WordPress (`content.rendered`). */
  html: string;
  className?: string;
  /**
   * Site key Turnstile. Se presente, ogni form CF7 viene corredato di un widget
   * Turnstile (coerente con `contact-us`/`events`). La verifica avviene lato
   * server in `/api/cf7/{formId}`.
   */
  turnstileSiteKey?: string;
}

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

function loadTurnstileScript(onReady: () => void) {
  const existing = document.getElementById(
    TURNSTILE_SCRIPT_ID
  ) as HTMLScriptElement | null;

  if (existing) {
    if (getTurnstile()) onReady();
    else existing.addEventListener("load", onReady);
    return;
  }

  const script = document.createElement("script");
  script.id = TURNSTILE_SCRIPT_ID;
  script.src =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  script.async = true;
  script.defer = true;
  script.addEventListener("load", onReady);
  document.head.appendChild(script);
}

/**
 * Renderizza contenuto HTML proveniente da WordPress, gestendo in modo sicuro
 * eventuali form Contact Form 7 incorporati:
 *  - l'`action` viene riscritta verso il proxy interno `/api/cf7/{id}` (già a
 *    livello di stringa, quindi anche nel sorgente SSR — l'URL WP non è esposto);
 *  - il submit è intercettato e inoltrato via `fetch`, mostrando i messaggi
 *    nativi di CF7 (`mail_sent`, `validation_failed`, ...);
 *  - se `turnstileSiteKey` è presente, inietta e renderizza un widget Turnstile.
 */
export default function WpContent({
  html,
  className,
  turnstileSiteKey,
}: WpContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Riscrittura pura: gira anche in SSR, così l'HTML iniziale è già sicuro.
  const safeHtml = useMemo(() => rewriteCf7FormActions(html), [html]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const forms = Array.from(
      container.querySelectorAll<HTMLFormElement>("form.wpcf7-form")
    );
    if (forms.length === 0) return;

    const cleanups: Array<() => void> = [];

    const hydrateForm = (form: HTMLFormElement) => {
      if (form.dataset.cf7Hydrated === "true") return;
      form.dataset.cf7Hydrated = "true";

      const formId =
        form.querySelector<HTMLInputElement>('input[name="_wpcf7"]')?.value ||
        form.getAttribute("action")?.match(/\/api\/cf7\/(\d+)/)?.[1];

      if (!formId) return;

      form.setAttribute("action", `/api/cf7/${formId}`);

      // Allinea il submit allo stile dell'app (Button variant "secondary").
      form
        .querySelectorAll<HTMLElement>(
          'input[type="submit"], button[type="submit"], .wpcf7-submit'
        )
        .forEach((btn) => {
          btn.className = cn(
            buttonVariants({ variant: "secondary" }),
            "mt-4 w-auto cursor-pointer",
            btn.className.includes("wpcf7-submit") ? "wpcf7-submit" : ""
          );
        });

      // Area messaggi: riuso quella di CF7 se presente, altrimenti la creo.
      let responseOutput =
        form.querySelector<HTMLElement>(".wpcf7-response-output");
      if (!responseOutput) {
        responseOutput = document.createElement("div");
        responseOutput.className = "wpcf7-response-output";
        form.appendChild(responseOutput);
      }

      const setResponse = (message: string, kind: "sent" | "invalid" | "error") => {
        responseOutput!.textContent = message;
        responseOutput!.hidden = !message;
        responseOutput!.classList.remove(
          "wpcf7-mail-sent-ok",
          "wpcf7-validation-errors",
          "wpcf7-spam-blocked"
        );
        if (!message) return;
        responseOutput!.classList.add(
          kind === "sent"
            ? "wpcf7-mail-sent-ok"
            : kind === "invalid"
              ? "wpcf7-validation-errors"
              : "wpcf7-spam-blocked"
        );
      };

      const clearFieldErrors = () => {
        form
          .querySelectorAll(".wpcf7-not-valid-tip")
          .forEach((el) => el.remove());
        form
          .querySelectorAll(".wpcf7-not-valid")
          .forEach((el) => el.classList.remove("wpcf7-not-valid"));
      };

      const showFieldErrors = (
        invalidFields: Array<{ field?: string; into?: string; message?: string }>
      ) => {
        invalidFields.forEach((f) => {
          const wrap = f.into
            ? form.querySelector<HTMLElement>(f.into)
            : f.field
              ? form.querySelector<HTMLElement>(`[name="${f.field}"]`)?.parentElement
              : null;
          const target = wrap ?? (f.field
            ? form.querySelector<HTMLElement>(`[name="${f.field}"]`)
            : null);
          if (!target || !f.message) return;
          const control = target.querySelector<HTMLElement>("[name]") ?? target;
          control.classList.add("wpcf7-not-valid");
          const tip = document.createElement("span");
          tip.className = "wpcf7-not-valid-tip text-destructive text-sm mt-1 block";
          tip.textContent = f.message;
          target.appendChild(tip);
        });
      };

      // Turnstile: rimuovo eventuali widget WP (sitekey potenzialmente diversa)
      // e inietto il nostro, coerente con la verifica server-side.
      let turnstileWidgetId: string | null = null;
      let turnstileHolder: HTMLElement | null = null;
      if (turnstileSiteKey) {
        form
          .querySelectorAll(".cf-turnstile")
          .forEach((el) => el.remove());
        turnstileHolder = document.createElement("div");
        turnstileHolder.className = "cf-turnstile my-4";
        const submitBtn = form.querySelector(
          'input[type="submit"], button[type="submit"], .wpcf7-submit'
        );
        if (submitBtn?.parentElement) {
          submitBtn.parentElement.insertBefore(turnstileHolder, submitBtn);
        } else {
          form.appendChild(turnstileHolder);
        }

        loadTurnstileScript(() => {
          const ts = getTurnstile();
          if (ts && turnstileHolder && !turnstileWidgetId) {
            turnstileWidgetId = ts.render(turnstileHolder, {
              sitekey: turnstileSiteKey,
            });
          }
        });
      }

      const onSubmit = async (event: Event) => {
        event.preventDefault();
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        clearFieldErrors();
        setResponse("", "sent");

        const submitButtons = Array.from(
          form.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
            'button[type="submit"], input[type="submit"]'
          )
        );
        submitButtons.forEach((b) => (b.disabled = true));
        form.classList.add("submitting");

        try {
          const resp = await fetch(`/api/cf7/${formId}`, {
            method: "POST",
            body: new FormData(form),
          });
          const data: any = await resp.json().catch(() => null);

          if (data?.status === "mail_sent") {
            form.reset();
            setResponse(
              data.message || t("Messaggio inviato con successo"),
              "sent"
            );
          } else if (data?.status === "validation_failed") {
            setResponse(
              data.message ||
                t("Si è verificato un errore. Controlla i campi e riprova."),
              "invalid"
            );
            if (Array.isArray(data.invalid_fields)) {
              showFieldErrors(data.invalid_fields);
            }
          } else {
            setResponse(
              data?.message ||
                t("Si è verificato un errore durante l'invio del modulo."),
              "error"
            );
          }
        } catch {
          setResponse(
            t("Si è verificato un errore durante l'invio del modulo."),
            "error"
          );
        } finally {
          submitButtons.forEach((b) => (b.disabled = false));
          form.classList.remove("submitting");
          const ts = getTurnstile();
          if (ts && turnstileWidgetId) ts.reset(turnstileWidgetId);
        }
      };

      form.addEventListener("submit", onSubmit);
      cleanups.push(() => {
        form.removeEventListener("submit", onSubmit);
        const ts = getTurnstile();
        if (ts && turnstileWidgetId) {
          try {
            ts.remove(turnstileWidgetId);
          } catch {
            /* noop */
          }
        }
        delete form.dataset.cf7Hydrated;
      });
    };

    forms.forEach(hydrateForm);

    return () => cleanups.forEach((fn) => fn());
  }, [safeHtml, turnstileSiteKey, t]);

  return (
    <div
      ref={containerRef}
      className={cn(className)}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}
